import React, { useContext, useState } from 'react';
import { DateTime } from 'luxon';

import { downloadPdfFromBlobResponse } from 'helpers/pdf';

import { media } from 'context';
import Avatar from 'ds/Avatar';
import Grid from 'ds/Grid';
import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import Lightbox from 'ds/Lightbox';
import MessagePdfThumbnail from 'ds/MessageThread/MessagePdfThumbnail';
import RichTextDisplay from 'ds/RichTextDisplay';
import Text from 'ds/Text';
import { namePartsToFirstLastInitial } from 'ds/utils';
import { Message as MessageType } from 'shared';
import axios from 'shared/lib/axios';

import MessageImageThumbnail from './MessageImageThumbnail';

interface Props {
  message: MessageType;
  showAbsoluteTime: boolean;
  onToggleTimeFormat: () => void;
  taskId?: number;
  messageDownloadPathPrefix?: string;
}

const Message: React.FC<Props> = ({
  message: { id, author_email, author, liked, body, images, files = [], created_at },
  showAbsoluteTime,
  onToggleTimeFormat,
  taskId,
  messageDownloadPathPrefix
}) => {
  const { avatar_url, first, last, customer } = author ?? {};
  const [lightboxStartingIndex, setLightboxStartingIndex] = useState<number>(-1);
  const { xs } = useContext(media);

  const likedOrDisliked = liked !== null;

  const bodyDisplay = !body && !!liked ? 'Gave positive feedback.' : likedOrDisliked ? `"${body}"` : body;

  const messageDate = DateTime.fromISO(created_at);
  const timeDisplay = showAbsoluteTime ? messageDate.toFormat('LLL d, h:mm a') : messageDate.toRelative();

  const canDownloadFiles = taskId !== undefined && messageDownloadPathPrefix !== undefined && files.length > 0;

  function downloadFile({ fileId }: { fileId: number }) {
    const url = `${messageDownloadPathPrefix}/${taskId}/messages/${id}/download_file?file_id=${fileId}`;
    return axios({
      url,
      method: 'GET',
      responseType: 'blob'
    }).then(response => {
      downloadPdfFromBlobResponse(response);
    });
  }

  return (
    <Layout flexGrow={1}>
      {lightboxStartingIndex >= 0 && (
        <Lightbox
          images={images}
          startingIndex={lightboxStartingIndex}
          onClose={() => setLightboxStartingIndex(-1)}
          padResize
        />
      )}
      <Layout direction="row" flexGrow={1}>
        <Avatar size={32} avatarUrl={avatar_url} borderWidth={0} />
        <Layout marginLeft={16} />
        <Layout direction="column" flexGrow={1}>
          <Layout direction="row" paddingY={6} justify="space-between" align="center">
            {!!author_email ? (
              <Text size="body-xs" color="gray-400">
                {author_email}
              </Text>
            ) : (
              <Text size="body-xs" color="gray-400">
                {namePartsToFirstLastInitial({ firstname: first!, lastname: last! })}
                {!!customer && ` • ${customer.name}`}
              </Text>
            )}
            <Layout flexShrink={0} align="center">
              <button
                onClick={onToggleTimeFormat}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 'inherit'
                }}
              >
                <Text size="body-xs" color="gray-400">
                  {timeDisplay}
                </Text>
              </button>
            </Layout>
          </Layout>
          <Layout marginTop={4} />
          <Layout direction="column">
            <Layout
              padding={likedOrDisliked ? 12 : undefined}
              color={likedOrDisliked ? 'gray-25' : undefined}
              borderRadius={likedOrDisliked ? 8 : undefined}
              align="flex-start"
            >
              {likedOrDisliked && (
                <Layout
                  width={32}
                  minWidth={32}
                  height={32}
                  minHeight={32}
                  borderRadius="circular"
                  color={liked ? 'blue-500' : 'gray-700'}
                  align="center"
                  justify="center"
                  marginRight={12}
                >
                  <Icon name={liked ? 'thumbsUpFilled' : 'thumbsDownFilled'} size="xs" color="white" />
                </Layout>
              )}
              <Layout marginTop={likedOrDisliked ? 6 : undefined}>
                <Text size="body-sm" color={likedOrDisliked ? 'gray-700' : undefined} italic={likedOrDisliked}>
                  <RichTextDisplay value={bodyDisplay} />
                </Text>
              </Layout>
            </Layout>
            <Layout marginTop={12} />
            {!!images.length && (
              <Layout marginTop={24} flex wrap position="relative" left={-6} width="calc(100% + 12px)">
                {images.map(({ key }, index) => {
                  return (
                    <MessageImageThumbnail
                      onClick={() => setLightboxStartingIndex(index)}
                      style={{
                        margin: xs ? '10px 6px' : '8px 6px',
                        width: 98,
                        height: 98
                      }}
                      key={key}
                      srcKey={key}
                    />
                  );
                })}
              </Layout>
            )}
            {canDownloadFiles && (
              <Layout
                marginTop={files.length > 0 && !images.length ? 16 : 0}
                flex
                wrap
                position="relative"
                left={-6}
                width="calc(100% + 12px)"
              >
                <Grid gap={8} itemsPerRow={1}>
                  {files.map(({ id: fileId, key, filename, filesize }) => {
                    return (
                      <MessagePdfThumbnail
                        onClick={() => {
                          downloadFile({ fileId });
                        }}
                        key={key}
                        filename={filename}
                        filesize={filesize}
                      />
                    );
                  })}
                </Grid>
              </Layout>
            )}
          </Layout>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default Message;
